// useMealPlanLoader.js — Orchestrates meal plan loading: localStorage restore, DB fetch, and streaming generation
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchMealPlan, getUserCountry, probeRunware } from "../../../services/apiService";
import { generationCostTracker, MODEL_CONFIG, getProviderForModel } from "../../../services/apiConfig";
import backendClient from "../../../services/backendClient";
import { getFingerprint } from "../../../utils/fingerprint";
import { filterByRestrictions } from "../../../utils/dietaryFilters";
import { TOTAL_EXPECTED_RECIPES } from "../../../hooks/useMealPlanGeneration";
import {
  initProgressiveProcessor,
  resetProgressiveProcessor,
  abortProgressiveProcessor,
  queueRecipeForProcessing,
  queueAllRecipesForProcessing,
  releaseWithMatchedProducts,
} from "../../../utils/progressiveRecipeProcessor";
import {
  saveInProgressMealPlan,
  getInProgressMealPlan,
  updateHaveItStateInStorage,
} from "../../../utils/inProgressMealPlan";
import { resetRecipeProcessingState } from "../recipeProcessingService";
import { generatePlaceholderRecipes } from "../utils/mealPlanHelpers";
import { computeRecipeFreshnessDays, mergeStorageDataIntoItem, accumulateStorageData } from "../../../utils/recipeFreshnessRules";
import { COOKING_TIME_LIMITS, COOKING_TIME_TOLERANCE_MIN, clampCookingTime, getPassiveCookingBonus } from "../../../utils/cookingTimeLimits";
import {
  trackMealSetGenerationSucceeded,
  trackMealSetGenerationFailed,
  trackMealPlanStructureInvalid,
  ERROR_STAGE,
  ERROR_CODE,
} from "../../../analytics";

// COOKING_TIME_LIMITS / COOKING_TIME_TOLERANCE_MIN are imported from
// src/utils/cookingTimeLimits.js — the single source of truth shared with the
// generation prompt. Do NOT redefine a stricter local copy here; that causes
// valid-per-prompt recipes to be rejected and the generation to hard-fail.

const EXPECTED_MEAL_TYPE_SEQUENCE = [
  'breakfast', 'breakfast', 'breakfast',
  'lunch', 'lunch', 'lunch',
  'dinner', 'dinner', 'dinner',
];

const parseCookingMinutes = (value) => {
  if (value === null || value === undefined) return null;
  const match = String(value).match(/\d+/);
  if (!match) return null;
  const minutes = Number(match[0]);
  return Number.isFinite(minutes) ? minutes : null;
};

const normalizeMealType = (value) => String(value || '').trim().toLowerCase();

const getMealTypeDistribution = (recipes) => {
  const distribution = { breakfast: 0, lunch: 0, dinner: 0, unknown: 0 };

  for (const recipe of recipes || []) {
    const mealType = normalizeMealType(recipe?.mealType);
    if (mealType === 'breakfast') distribution.breakfast += 1;
    else if (mealType === 'lunch') distribution.lunch += 1;
    else if (mealType === 'dinner') distribution.dinner += 1;
    else distribution.unknown += 1;
  }

  return distribution;
};

const formatMealTypeDistribution = ({ breakfast, lunch, dinner, unknown }) =>
  `${breakfast}B/${lunch}L/${dinner}D${unknown ? `/${unknown} unknown` : ''}`;

const getMealTypeSequence = (recipes) => (recipes || []).map(recipe => normalizeMealType(recipe?.mealType) || 'unknown');

const formatMealTypeSequence = (recipes) =>
  getMealTypeSequence(recipes).map((mealType, index) => `${index + 1}:${mealType}`).join(', ');

const getMealTypePositionIssues = (recipes) => getMealTypeSequence(recipes)
  .map((mealType, index) => ({
    position: index + 1,
    expected: EXPECTED_MEAL_TYPE_SEQUENCE[index] || 'none',
    actual: mealType,
  }))
  .filter(item => item.expected !== item.actual);

const getResponseLengthBucket = (length = 0) => {
  if (!length) return '0';
  if (length < 2000) return '<2k';
  if (length < 5000) return '2k-5k';
  if (length < 10000) return '5k-10k';
  if (length < 20000) return '10k-20k';
  return '20k+';
};

const isPartialParseStatus = (parseStatus) =>
  parseStatus === 'partial-recovery' ||
  parseStatus === 'partial-after-error' ||
  parseStatus === 'empty-response';

const DISALLOWED_OVERNIGHT_RECIPE_PATTERN = /\b(overnight|night before|previous night|all night|soaked oats|overnight oats|overnight marinade|avena nocturna|avena notturna|de un d[ií]a para otro|d'un dia per l'altre|durante la noche|toda la noche|noche anterior|tota la nit|nit abans|toute la nuit|la veille|peste noapte|durante la notte|tutta la notte|sera prima)\b|\b(на ночь|на ніч|всю ночь|всю ніч|с вечера|з вечора|ночная овсянка|нічна вівсянка|овсянка на ночь|вівсянка на ніч)\b/i;
const SLOW_BREAKFAST_CONCEPT_PATTERN = /\b(chia pudding|chia bowl|chilled|set|panna cotta|mousse)\b/i;

const getMealPlanStructureIssue = (recipes) => {
  if (!Array.isArray(recipes) || recipes.length === 0) {
    return { reason: 'empty meal plan', distribution: getMealTypeDistribution([]) };
  }

  const distribution = getMealTypeDistribution(recipes);
  const hasWrongCount = recipes.length !== TOTAL_EXPECTED_RECIPES;
  const hasWrongDistribution = distribution.breakfast !== 3 || distribution.lunch !== 3 || distribution.dinner !== 3 || distribution.unknown > 0;

  if (!hasWrongCount && !hasWrongDistribution) return null;

  return {
    reason: hasWrongCount ? `expected ${TOTAL_EXPECTED_RECIPES} recipes, got ${recipes.length}` : 'invalid meal type distribution',
    distribution,
  };
};

const getCookingTimeViolations = (recipes, cookingStyle) => {
  if (!Array.isArray(recipes)) return [];

  return recipes.flatMap(recipe => {
    const mealType = normalizeMealType(recipe?.mealType);
    const complexity = cookingStyle?.[mealType]?.cookingTime || 'standard';
    const limit = COOKING_TIME_LIMITS[mealType]?.[complexity];
    const minutes = parseCookingMinutes(recipe?.cookingTime || recipe?.cooking_time);
    const title = recipe?.title || 'Untitled meal';
    const description = recipe?.description || '';
    const recipeText = `${title} ${description}`;

    if (!limit) return [];

    if (DISALLOWED_OVERNIGHT_RECIPE_PATTERN.test(recipeText)) {
      return [{
        title,
        mealType: recipe?.mealType || 'Unknown',
        minutes: minutes || limit,
        limit,
        complexity,
        reason: 'overnight recipes are not allowed',
      }];
    }

    if (mealType === 'breakfast' && limit < 35 && SLOW_BREAKFAST_CONCEPT_PATTERN.test(recipeText)) {
      return [{
        title,
        mealType: recipe?.mealType || 'Unknown',
        minutes: minutes || limit + 1,
        limit,
        complexity,
        reason: 'hidden passive breakfast format',
      }];
    }

    // Allow a small overshoot above the stated cap before flagging a numeric
    // violation — LLMs routinely exceed a told cap by a couple of minutes, and
    // hard-failing a whole 9-recipe set over +2 min is bad UX. The overnight /
    // slow-breakfast concept bans above stay strict (no tolerance).
    const effectiveLimit = limit + getPassiveCookingBonus(mealType) + COOKING_TIME_TOLERANCE_MIN;
    if (!minutes || minutes <= effectiveLimit) return [];

    return [{
      title,
      mealType: recipe?.mealType || 'Unknown',
      minutes,
      limit,
      complexity,
    }];
  });
};

const getDietaryRestrictionKeysForValidation = (dietaryPreferences) => {
  if (!dietaryPreferences) return [];

  if (typeof dietaryPreferences === 'string') {
    const value = dietaryPreferences.toLowerCase();
    const keys = [];
    if (value.includes('halal')) keys.push('halal');
    if (value.includes('pork')) keys.push('noPork');
    if (value.includes('dairy')) keys.push('noDairy');
    if (value.includes('egg')) keys.push('noEggs');
    if (value.includes('gluten')) keys.push('glutenFree');
    return keys;
  }

  if (!Array.isArray(dietaryPreferences)) return [];

  return dietaryPreferences
    .filter(pref => pref?.key && !pref.isNoneOption && pref.key !== 'none' && pref.key !== 'noneFood' && pref.key !== 'noneDiet')
    .map(pref => pref.key);
};

const getDietaryRestrictionViolations = (recipes, dietaryPreferences) => {
  const restrictionKeys = getDietaryRestrictionKeysForValidation(dietaryPreferences);
  if (!Array.isArray(recipes) || restrictionKeys.length === 0) return [];

  return recipes.flatMap(recipe => {
    const ingredientNames = Array.isArray(recipe?.ingredients)
      ? recipe.ingredients.map(item => item?.product || item?.name || '').filter(Boolean)
      : [];
    const fields = [recipe?.title, recipe?.description, ...ingredientNames]
      .filter(Boolean)
      .map(value => String(value));
    const offending = fields.filter(value => filterByRestrictions([value], restrictionKeys).length === 0);

    if (offending.length === 0) return [];

    return [{
      title: recipe?.title || 'Untitled meal',
      mealType: recipe?.mealType || 'Unknown',
      offending: Array.from(new Set(offending)).slice(0, 5),
    }];
  });
};

/**
 * Manages the full meal plan loading lifecycle:
 * 1. Check localStorage for in-progress data (page reload recovery)
 * 2. Check backend for saved meal plan (authenticated users)
 * 3. Consume a try + stream a new plan from OpenAI
 *
 * @param {Object} deps
 * @param {boolean} deps.isFromWizard
 * @param {React.MutableRefObject<boolean>} deps.isFromWizardRef
 * @param {Object} deps.mealPlanCtx - All needed values from useMealPlan
 * @param {Object} deps.userCtx - { user, budget, numberOfPeople, selectedCuisine, dietaryPreferences, cookingStyle }
 * @param {Function} deps.generateGroceryList
 * @param {Function} deps.addReasoningStep
 * @param {Function} deps.clearReasoningSteps
 * @param {string|null} deps.userCountry
 * @param {Function} deps.setImagesLoading
 * @param {Function} deps.setGroceryListLoading
 * @param {React.MutableRefObject<boolean>} deps.isMountedRef
 * @param {Function} deps.onNavigateToWizard
 * @param {Function} deps.t - i18n translation function
 */
export function useMealPlanLoader({
  isFromWizard,
  isFromWizardRef,
  mealPlanCtx,
  userCtx,
  generateGroceryList,
  addReasoningStep,
  clearReasoningSteps,
  userCountry,
  setImagesLoading,
  setGroceryListLoading,
  isMountedRef,
  onNavigateToWizard,
  t,
}) {
  const navigate = useNavigate();

  const {
    mealPlan, setMealPlan,
    totalCost, setTotalCost,
    productList, setProductList,
    setMaxStorageTime,
    adjustedTotalCost, setAdjustedTotalCost,
    haveItState, setHaveItState,
    leftovers, setLeftovers,
    resetMealPlan,
    startStreaming, updateStreamProgress, endStreaming,
  } = mealPlanCtx;

  const { user, budget, numberOfPeople, selectedCuisine, dietaryPreferences, cookingStyle } = userCtx;

  // ─── State ────────────────────────────────────────────────
  const [loadingState, setLoadingState] = useState("checking");
  const [loadingPhase, setLoadingPhase] = useState("generating");
  const [loadingRecipesReady, setLoadingRecipesReady] = useState(0);
  const [wasLoadedFromDb, setWasLoadedFromDb] = useState(false);
  const [skipLocalStorageSave, setSkipLocalStorageSave] = useState(false);
  const [skipSavedMealPlan, setSkipSavedMealPlan] = useState(false);
  const [restoredFromLocalStorage, setRestoredFromLocalStorage] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [triesLeft, setTriesLeft] = useState(null);
  const [replacementsRemaining, setReplacementsRemaining] = useState(null);
  const [maxReplacements, setMaxReplacements] = useState(null);
  const [currentDescriptionIndex, setCurrentDescriptionIndex] = useState(0);

  const isRestoringFromLocalStorage = useRef(false);
  const hasFetchedMealPlan = useRef(false);
  const recipesCount = useRef(0);
  const deviceIdRef = useRef(null);
  const generationAttemptRef = useRef(0);
  // Accumulates ingredient_storage data from recipe details as they complete.
  // Used to enrich productList after grocery list generation (recipe details often finish before grocery list).
  const storageDataRef = useRef(new Map());

  // ─── Keep selectedRecipe in sync (moved here since it uses mealPlan) - handled in MealPlan.js ───

  // ─── Fetch premium status on mount ────────────────────────
  useEffect(() => {
    const fetchPremiumStatus = async () => {
      try {
        const params = new URLSearchParams();
        const authToken = localStorage.getItem("authToken");
        if (!authToken) {
          const fp = getFingerprint();
          if (fp) params.set("fingerprint", fp);
        }
        const qs = params.toString();
        const data = await backendClient.get(`/api/meal-plan-limit/check${qs ? `?${qs}` : ""}`);
        if (data.isPremium !== undefined) setIsPremium(data.isPremium);
        if (data.isAdmin !== undefined) setIsAdmin(data.isAdmin);
        if (data.triesRemaining !== undefined && data.triesRemaining !== 'unlimited') {
          setTriesLeft(data.triesRemaining);
        }
        if (data.deviceId) deviceIdRef.current = data.deviceId;
      } catch (error) {
        console.warn("[useMealPlanLoader] Error fetching premium status:", error.message);
      }
    };
    fetchPremiumStatus();
  }, []);

  // ─── Fetch replacement counters + refresh on replacementConsumed event ────
  useEffect(() => {
    const fetchReplacementCounters = async () => {
      try {
        const params = new URLSearchParams();
        const authToken = localStorage.getItem("authToken");
        if (!authToken) {
          const fp = getFingerprint();
          if (fp) params.set("fingerprint", fp);
        }
        const qs = params.toString();
        const data = await backendClient.get(`/api/meal-plan-limit/check-replacements${qs ? `?${qs}` : ""}`);
        if (typeof data.replacementsRemaining === 'number') {
          setReplacementsRemaining(data.replacementsRemaining);
        }
        if (typeof data.maxReplacements === 'number') {
          setMaxReplacements(data.maxReplacements);
        }
      } catch (error) {
        console.warn("[useMealPlanLoader] Error fetching replacement counters:", error.message);
      }
    };
    fetchReplacementCounters();

    const handleReplacementConsumed = () => fetchReplacementCounters();
    window.addEventListener('replacementConsumed', handleReplacementConsumed);
    return () => window.removeEventListener('replacementConsumed', handleReplacementConsumed);
  }, []);

  // ─── Rotating descriptions ────────────────────────────────
  const rotatingDescriptions = t('rotatingDescriptions', { returnObjects: true }) || [];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDescriptionIndex(prev => (prev + 1) % (rotatingDescriptions.length || 1));
    }, 10000);
    return () => clearInterval(interval);
  }, [rotatingDescriptions.length]);

  // ─── Loading progress CSS var ─────────────────────────────
  const setLoadingProgress = useCallback((progress) => {
    document.documentElement.style.setProperty("--loading-progress", `${progress}%`);
  }, []);

  // ─── Mount: restore from localStorage or reset ────────────
  useEffect(() => {
    if (isFromWizard) {
      console.log("[useMealPlanLoader] Coming from wizard - fresh generation");
      setTotalCost("");
      setAdjustedTotalCost(null);
      setSkipSavedMealPlan(true);
      return;
    }

    const inProgressData = getInProgressMealPlan();
    if (inProgressData && inProgressData.mealPlan && inProgressData.mealPlan.length > 0) {
      console.log("[useMealPlanLoader] Found in-progress meal plan, restoring...", {
        mealPlanLength: inProgressData.mealPlan.length,
        productListLength: inProgressData.productList?.length || 0,
      });

      isRestoringFromLocalStorage.current = true;
      setMealPlan(inProgressData.mealPlan);
      if (inProgressData.productList && inProgressData.productList.length > 0) {
        setProductList(inProgressData.productList);
      }
      setTotalCost(inProgressData.totalCost || "");
      setAdjustedTotalCost(inProgressData.adjustedTotalCost || null);
      if (inProgressData.maxStorageTime) setMaxStorageTime(inProgressData.maxStorageTime);
      if (inProgressData.leftovers) setLeftovers(inProgressData.leftovers);
      if (inProgressData.haveItState && Object.keys(inProgressData.haveItState).length > 0) {
        setHaveItState(inProgressData.haveItState);
      }
      setLoadingState("complete");
      setSkipSavedMealPlan(true);
      setRestoredFromLocalStorage(true);
      hasFetchedMealPlan.current = true;
      setSkipLocalStorageSave(true);
      return;
    }

    setTotalCost("");
    setAdjustedTotalCost(null);
    setSkipSavedMealPlan(true);
    if (mealPlan.length === 0) resetMealPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Clear costs when meal plan becomes empty ─────────────
  useEffect(() => {
    if (restoredFromLocalStorage || isRestoringFromLocalStorage.current) return;
    if (mealPlan.length === 0) {
      setTotalCost("");
      setAdjustedTotalCost(null);
    }
  }, [mealPlan.length, setTotalCost, setAdjustedTotalCost, restoredFromLocalStorage]);

  // ─── Save in-progress to localStorage when fully complete ─
  useEffect(() => {
    const allRecipesLoaded = mealPlan.length === 9 && mealPlan.every(r => !r.isPlaceholder);
    const allImagesLoaded = mealPlan.length === 9 && mealPlan.every(r =>
      (r.imageURL && r.imageURL.length > 0) ||
      r.imageFailed ||
      (!r.loadingStatus && r.instructionsFormatted)
    );
    const groceryListLoaded = productList && productList.length > 0 && totalCost && totalCost.length > 0;
    const isFullyComplete = allRecipesLoaded && allImagesLoaded && groceryListLoaded;

    if (isFullyComplete && !wasLoadedFromDb && !restoredFromLocalStorage && !skipLocalStorageSave) {
      console.log("[useMealPlanLoader] Saving COMPLETE meal plan to localStorage");
      saveInProgressMealPlan({
        mealPlan, productList, totalCost, adjustedTotalCost, maxStorageTime: mealPlanCtx.maxStorageTime, leftovers,
        haveItState,
      });
    }
  }, [mealPlan, productList, totalCost, adjustedTotalCost, mealPlanCtx.maxStorageTime, leftovers, haveItState, wasLoadedFromDb, restoredFromLocalStorage, skipLocalStorageSave]);

  // ─── Persist haveItState changes to existing localStorage data ─
  useEffect(() => {
    if (Object.keys(haveItState).length === 0) return;
    updateHaveItStateInStorage(haveItState);
  }, [haveItState]);

  // ─── Main load effect ─────────────────────────────────────
  useEffect(() => {
    const loadPlan = async () => {
      if (restoredFromLocalStorage) return;
      if (!isMountedRef.current) return;

      const token = localStorage.getItem("authToken");

      // Try loading saved meal plan from backend
      if (token) {
        try {
          if (!skipSavedMealPlan && !isFromWizardRef.current) {
            const data = await backendClient.get("/api/mealplan");
            if (data && data.mealPlan && data.mealPlan.length > 0) {
              setMealPlan(data.mealPlan);
              if (data.productList) setProductList(data.productList);
              if (data.totalCost) setTotalCost(data.totalCost);
              if (data.leftovers) setLeftovers(data.leftovers);
              if (data.haveItState) setHaveItState(data.haveItState);
              setLoadingState("complete");
              setWasLoadedFromDb(true);
              hasFetchedMealPlan.current = true;
              return;
            }
          }
        } catch (error) {
          console.error("Error checking saved meal plan:", error);
        }
      }

      // Limit check
      const userId = user ? user._id : null;
      const fingerprint = getFingerprint();
      const queryParams = {};
      if (userId) queryParams.userId = userId;
      if (fingerprint) queryParams.fingerprint = fingerprint;
      const queryString = new URLSearchParams(queryParams).toString();
      const limitEndpoint = `/api/meal-plan-limit/check${queryString ? `?${queryString}` : ""}`;

      try {
        const limitData = await backendClient.get(limitEndpoint);
        if (limitData.deviceId) deviceIdRef.current = limitData.deviceId;
        if (limitData.isPremium !== undefined) setIsPremium(limitData.isPremium);
        if (limitData.isAdmin !== undefined) setIsAdmin(limitData.isAdmin);
        if (limitData.triesRemaining !== undefined && limitData.triesRemaining !== 'unlimited') {
          setTriesLeft(limitData.triesRemaining);
        }
        // Redirect to /premium only when the user has truly run out of tries.
        // Trust `triesRemaining` over `hasReachedLimit` — the backend has been
        // observed to set `hasReachedLimit=true` while `triesRemaining` is still 1
        // (off-by-one), which would paywall a user with a remaining try.
        // Premium / admin / 'unlimited' users should never be redirected here.
        const remaining = limitData.triesRemaining;
        const trulyOutOfTries =
          !limitData.isPremium &&
          !limitData.isAdmin &&
          remaining !== 'unlimited' &&
          (remaining === 0 || (remaining === undefined && limitData.hasReachedLimit));
        if (remaining > 0 && limitData.hasReachedLimit) {
          console.warn(
            `[useMealPlanLoader] Backend mismatch: triesRemaining=${remaining} but hasReachedLimit=true. Trusting triesRemaining.`
          );
        }
        if (trulyOutOfTries) {
          navigate('/premium');
          return;
        }
      } catch (error) {
        if (!error.isAuthError) {
          console.warn("[useMealPlanLoader] Error checking limit:", error.message);
        }
      }

      // Fresh generation
      setLoadingState("initial");
      setLoadingPhase("generating");
      setLoadingRecipesReady(0);
      setLoadingProgress(0);
      clearReasoningSteps();
      // Reset cost tracker once per user-visible generation, so any internal
      // structure-retry attempts accumulate into the same summary.
      generationCostTracker.reset();
      resetRecipeProcessingState();
      setProductList([]);
      setTotalCost("");
      setAdjustedTotalCost(null);
      setGroceryListLoading(true);
      setImagesLoading({});
      setLeftovers([]);
      setSkipSavedMealPlan(true);
      recipesCount.current = 0;
      storageDataRef.current = new Map();
      startStreaming();

      let receivedFirstRecipe = false;
      let completeMealPlan = [];
      const generationStartTime = Date.now();
      const effectiveCountry = await getUserCountry();
      let currentAttemptId = generationAttemptRef.current + 1;
      generationAttemptRef.current = currentAttemptId;

      const isCurrentAttempt = (attemptId) => isMountedRef.current && generationAttemptRef.current === attemptId;

      const startNextAttempt = () => {
        currentAttemptId = generationAttemptRef.current + 1;
        generationAttemptRef.current = currentAttemptId;
        return currentAttemptId;
      };

      // Per-recipe immediate processing: match each recipe to Typesense as soon as it's parsed,
      // then immediately queue recipe details + image generation.
      // With MAX_CONCURRENT_IMAGES=5 and natural stream stagger (~1-2s between recipes),
      // Runware never sees more than 3-4 simultaneous requests.
      let recipeIndex = 0; // global position counter for minStorageDays

      // Recipes are queued immediately as they stream but held in the processor
      // until Typesense matching completes. At that point releaseWithMatchedProducts
      // stamps every queued recipe with real consolidated product data and fires all
      // GPT calls in parallel. This replaces the old per-recipe matchIngredientsForBatch
      // approach that used lightweight stale data.
      const dispatchRecipe = (recipe, attemptId) => {
        if (!isCurrentAttempt(attemptId)) {
          console.log(`[MealPlan] ⏹️ Ignoring stale recipe dispatch "${recipe?.title}" for attempt ${attemptId}; current=${generationAttemptRef.current}`);
          return;
        }
        const idx = recipeIndex++;
        const mealTypeKey = (recipe.mealType || '').toLowerCase();
        const complexity = cookingStyle?.[mealTypeKey]?.cookingTime || 'standard';
        const cookFor2Days = cookingStyle?.[mealTypeKey]?.cookFor2Days || false;
        const minStorageDays = 3 + (idx % 3);

        console.log(`[MealPlan] 📥 [attempt ${attemptId}] Recipe #${idx + 1} "${recipe.title}" → queuing (held until Typesense matching)`);
        queueRecipeForProcessing(
          { ...recipe, minStorageDays, complexity, numberOfPeople, cookFor2Days },
          effectiveCountry,
          [],
          { generationId: attemptId }
        );
      };

      // Initialize progressive processor
      resetProgressiveProcessor({ generationId: currentAttemptId });

      const progressiveCallbacks = {
        onRecipeDetailsStart: (recipe) => {
          console.log(`[MealPlan] 📖 Starting details for "${recipe.title}"`);
          setImagesLoading(prev => ({ ...prev, [recipe.title]: true }));
          setMealPlan(prev => prev.map(r =>
            r.title === recipe.title ? { ...r, fetchingDetailsForImage: true } : r
          ));
        },
        onRecipeDetailsComplete: (recipeTitle, instructions) => {
          console.log(`[MealPlan] ✓ Details complete for "${recipeTitle}"`);
          const updatedTitle = instructions?.updated_title?.trim();
          const titleChanged = updatedTitle && updatedTitle !== recipeTitle;
          const fullDesc = instructions?.full_description;

          setMealPlan(prev => prev.map(r =>
            r.title === recipeTitle ? {
              ...r,
              instructions,
              instructionsFormatted: true,
              fetchingDetailsForImage: false,
              loadingStatus: (r.imageURL || r.imageFailed) ? null : 'image',
              ...(titleChanged ? { title: updatedTitle, originalTitle: r.originalTitle || recipeTitle } : {}),
              ...(fullDesc ? { description: fullDesc } : {}),
              cookingTime: clampCookingTime(
                instructions?.cooking_time,
                r.cookingTime,
                r.mealType,
                cookingStyle?.[normalizeMealType(r.mealType)]?.cookingTime || 'standard'
              ),
              maxStorageTime: (() => {
                const days = computeRecipeFreshnessDays(instructions);
                return days ? `${days}` : (instructions?.max_storage_time || r.maxStorageTime);
              })(),
            } : r
          ));

          if (titleChanged) {
            setImagesLoading(prev => {
              const next = { ...prev };
              const imageAlreadyLoaded = prev[recipeTitle] === false || prev[updatedTitle] === false;
              const hadInFlightLoading = Boolean(prev[recipeTitle]) || Boolean(prev[updatedTitle]);
              delete next[recipeTitle];
              next[updatedTitle] = imageAlreadyLoaded ? false : hadInFlightLoading;
              return next;
            });
          }

          // Update products with storage data
          if (instructions?.ingredient_storage && Array.isArray(instructions.ingredient_storage)) {
            // Accumulate storage data for post-grocery enrichment (recipe details often finish before grocery list).
            // Use accumulateStorageData so a fish used across several dishes keeps the safest storage
            // (freezer wins over fridge, longest planned freshness retained) regardless of completion order.
            for (const entry of instructions.ingredient_storage) {
              if (entry.name && entry.storage) {
                const days = Number(entry.shelf_life_days);
                const key = entry.name.toLowerCase().trim();
                const incoming = {
                  storage: entry.storage,
                  shelfLifeDays: days > 0 ? days : null,
                };
                storageDataRef.current.set(
                  key,
                  accumulateStorageData(storageDataRef.current.get(key), incoming, entry.name)
                );
              }
            }

            setProductList(prevProducts => {
              if (!prevProducts || prevProducts.length === 0) return prevProducts;
              const storageMap = new Map();
              for (const entry of instructions.ingredient_storage) {
                if (entry.name && entry.storage) {
                  const days = Number(entry.shelf_life_days);
                  storageMap.set(entry.name.toLowerCase().trim(), {
                    storage: entry.storage,
                    shelfLifeDays: days > 0 ? days : null,
                  });
                }
              }
              if (storageMap.size === 0) return prevProducts;

              return prevProducts.map(cat => ({
                ...cat,
                items: cat.items.map(item => {
                  const productName = (item.productName || item.name || '').toLowerCase().trim();
                  const originalName = (item.originalName || '').toLowerCase().trim();
                  let storageData = null;
                  for (const [ingName, data] of storageMap) {
                    if (productName.includes(ingName) || ingName.includes(productName) ||
                        (originalName && (originalName.includes(ingName) || ingName.includes(originalName)))) {
                      storageData = data;
                      break;
                    }
                  }
                  if (storageData) {
                    return mergeStorageDataIntoItem(item, storageData);
                  }
                  return item;
                }),
              }));
            });
          }
        },
        onImageComplete: (recipeTitle, imageURL, originalTitle) => {
          console.log(`[MealPlan] 🖼️ Image ready for "${recipeTitle}"${originalTitle && originalTitle !== recipeTitle ? ` (original: "${originalTitle}")` : ''}`);
          setMealPlan(prev => prev.map(r => {
            // Match by current title, original title, or the originalTitle field on the recipe
            const isMatch = r.title === recipeTitle ||
              (originalTitle && (r.title === originalTitle || r.originalTitle === originalTitle));
            return isMatch ? { ...r, imageURL, imageFailed: false, loadingStatus: null } : r;
          }));
          setImagesLoading(prev => {
            const next = { ...prev, [recipeTitle]: false };
            if (originalTitle && originalTitle !== recipeTitle) {
              next[originalTitle] = false;
            }
            return next;
          });
        },
        onError: (recipeTitle, error, originalTitle) => {
          console.error(`[MealPlan] ✗ Error processing "${recipeTitle}":`, error);
          setMealPlan(prev => prev.map(r => {
            const isMatch = r.title === recipeTitle ||
              (originalTitle && (r.title === originalTitle || r.originalTitle === originalTitle));
            if (!isMatch) return r;

            const isImageOnlyError = Boolean(originalTitle) ||
              /image generation|visual_description/i.test(error?.message || '');
            const shouldUseFallbackDetails = !r.instructions && !isImageOnlyError;

            const fallbackInstructions = shouldUseFallbackDetails ? {
              updated_title: r.title,
              full_description: r.description || 'Recipe details could not be loaded.',
              cooking_time: r.cookingTime || '',
              max_storage_time: r.maxStorageTime || '',
              ingredient_storage: [],
              cooking_instructions: [],
              tips: [],
              equipment: [],
              _partial: true,
              error: error?.message || 'Recipe processing failed',
            } : r.instructions;

            return {
              ...r,
              ...(fallbackInstructions ? { instructions: fallbackInstructions } : {}),
              instructionsFormatted: fallbackInstructions ? true : r.instructionsFormatted,
              fetchingDetailsForImage: isImageOnlyError && !r.instructions ? r.fetchingDetailsForImage : false,
              loadingStatus: null,
              imageFailed: r.imageURL ? false : true,
            };
          }));
          setImagesLoading(prev => {
            const next = { ...prev, [recipeTitle]: false };
            if (originalTitle && originalTitle !== recipeTitle) {
              next[originalTitle] = false;
            }
            return next;
          });
        },
      };
      initProgressiveProcessor(progressiveCallbacks, { generationId: currentAttemptId });

      // Consume try
      const incrementBody = {};
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        incrementBody.deviceId = deviceIdRef.current;
        const fp = getFingerprint();
        if (fp) incrementBody.fingerprint = fp;
      }

      if (authToken || incrementBody.deviceId || incrementBody.fingerprint) {
        try {
          await backendClient.post("/api/meal-plan-limit/consume-try", incrementBody, { timeout: 5000 });
          window.dispatchEvent(new CustomEvent("tryConsumed"));
        } catch (error) {
          if (
            error.status === 429 ||
            (error.status === 403 && (error.data?.error?.toLowerCase().includes("tries") ||
              error.data?.error?.toLowerCase().includes("limit") || error.data?.code === "NO_TRIES_REMAINING"))
          ) {
            navigate('/premium');
            return;
          }
          if (error.isAuthError) {
            console.log("🔐 Token expired during consume-try, clearing");
          }
        }
      }

      probeRunware();

      // Retry loop: if AI returns wrong meal type distribution or violates hard time limits, retry internally.
      // Never show an invalid meal set to the user after retries are exhausted.
      const MAX_STRUCTURE_RETRIES = 2;
      let structureRetryCount = 0;
      let retryReason = 'invalid meal plan constraints';

      const runGeneration = async () => {
        const attemptId = currentAttemptId;
        // Reset state for retry
        if (structureRetryCount > 0) {
          console.log(`[MealPlan] 🔄 Retrying generation (attempt ${structureRetryCount + 1}, generation=${attemptId}) due to ${retryReason}`);
          addReasoningStep(`Meal plan violated constraints (${retryReason}), retrying generation...`);
          resetProgressiveProcessor({ generationId: attemptId });
          initProgressiveProcessor(progressiveCallbacks, { generationId: attemptId });
          recipeIndex = 0;
          receivedFirstRecipe = false;
          completeMealPlan = [];
          recipesCount.current = 0;
          storageDataRef.current = new Map();
          setLoadingPhase("generating");
          setLoadingRecipesReady(0);
          setLoadingProgress(0);
          setMealPlan([]);
          setProductList([]);
          setImagesLoading({});
          setLeftovers([]);
          setTotalCost("");
          setAdjustedTotalCost(null);
          setGroceryListLoading(true);
        }

        console.log(`[MealPlan] ▶ Generation attempt ${structureRetryCount + 1} started (generation=${attemptId})`);

        const generationResult = await fetchMealPlan({
          userCountry: effectiveCountry,
          userBudget: budget,
          numberOfPeople,
          selectedCuisine,
          dietaryPreferences,
          cookingStyle,

          onRecipeComplete: (recipe) => {
            if (!isCurrentAttempt(attemptId)) {
              console.log(`[MealPlan] ⏹️ Ignoring stale onRecipeComplete "${recipe.title}" for attempt ${attemptId}; current=${generationAttemptRef.current}`);
              return;
            }
            console.log(`[MealPlan] 🍽️ [attempt ${attemptId}] Recipe "${recipe.title}" parsed → dispatching immediately`);
            dispatchRecipe(recipe, attemptId);
          },

          onPartialResponse: (partialData) => {
            if (!isCurrentAttempt(attemptId)) return;
            if (partialData.mealPlan?.length > 0) {
              if (!receivedFirstRecipe) {
                receivedFirstRecipe = true;
                setLoadingPhase("streaming");
                setLoadingRecipesReady(1);
                const initialRecipes = generatePlaceholderRecipes(t, TOTAL_EXPECTED_RECIPES);
                initialRecipes[0] = partialData.mealPlan[0];
                setMealPlan(initialRecipes);
                recipesCount.current = 1;
                updateStreamProgress({ recipes: 1 });
                completeMealPlan = [...partialData.mealPlan];
              } else {
                setMealPlan(prevMealPlan => {
                  const updatedMealPlan = [...prevMealPlan];
                  partialData.mealPlan.forEach((recipe, idx) => {
                    if (idx < updatedMealPlan.length && updatedMealPlan[idx]?.isPlaceholder) {
                      updatedMealPlan[idx] = recipe;
                      recipesCount.current++;
                      if (!completeMealPlan.some(r => r.title === recipe.title)) {
                        completeMealPlan.push(recipe);
                      }
                    }
                  });
                  return updatedMealPlan;
                });

                if (recipesCount.current <= 9) {
                  updateStreamProgress({ recipes: recipesCount.current });
                  setLoadingRecipesReady(recipesCount.current);
                }
              }

              setLoadingProgress(
                Math.min((partialData.mealPlan.length / TOTAL_EXPECTED_RECIPES) * 80, 80)
              );
            }

            if (partialData.maxStorageTime) {
              setMaxStorageTime(partialData.maxStorageTime);
            }
          },

          onRecipe: (recipe) => {
            if (!isCurrentAttempt(attemptId)) return;
            recipesCount.current += 1;
            updateStreamProgress({ recipes: recipesCount.current });
            setLoadingRecipesReady(recipesCount.current);

            setMealPlan(prevMealPlan => {
              const placeholderIdx = prevMealPlan.findIndex(r => r.isPlaceholder);
              if (placeholderIdx !== -1) {
                const updatedMealPlan = [...prevMealPlan];
                updatedMealPlan[placeholderIdx] = recipe;
                if (!completeMealPlan.some(r => r.title === recipe.title)) {
                  completeMealPlan.push(recipe);
                }
                return updatedMealPlan;
              }
              return prevMealPlan;
            });
          },

          onReasoning: (reasoningText) => {
            if (!isCurrentAttempt(attemptId)) return;
            if (reasoningText?.trim()) addReasoningStep(reasoningText);
          },
        });

        if (!isCurrentAttempt(attemptId)) {
          console.log(`[MealPlan] ⏹️ Ignoring completed stale generation attempt ${attemptId}; current=${generationAttemptRef.current}`);
          return;
        }

        const parseMetadata = generationResult?._mealPlanParse || {};
        const validationSource = Array.isArray(generationResult?.mealPlan)
          ? 'generationResult.mealPlan'
          : 'completeMealPlan';
        const completionModel = parseMetadata.model || MODEL_CONFIG.completion;
        const completionProvider = parseMetadata.provider || getProviderForModel(completionModel);

        const finalMealPlan = Array.isArray(generationResult?.mealPlan)
          ? generationResult.mealPlan
          : completeMealPlan;

        if (finalMealPlan.length > 0) {
          completeMealPlan = finalMealPlan;
        }

        // Validate final parsed payload: exactly 9 recipes and exactly 3 Breakfast, 3 Lunch, 3 Dinner.
        if (completeMealPlan.length > 0) {
          const structureIssue = getMealPlanStructureIssue(completeMealPlan);
          const mealTypeSequence = getMealTypeSequence(completeMealPlan);
          const mealTypeSequenceText = formatMealTypeSequence(completeMealPlan);
          const positionIssues = getMealTypePositionIssues(completeMealPlan);
          const parseStatus = parseMetadata.parseStatus || 'unknown';
          const failureReason = parseMetadata.isPartial || isPartialParseStatus(parseStatus)
            ? 'parse_or_partial'
            : 'structure';

          if (structureIssue) {
            const distributionSummary = formatMealTypeDistribution(structureIssue.distribution);
            console.warn(`[MealPlan] ⚠️ Invalid meal plan structure (${failureReason}): ${structureIssue.reason}; distribution=${distributionSummary} (expected 3B/3L/3D); source=${validationSource}; parse=${parseStatus}; sequence=${mealTypeSequenceText}`);
            try {
              trackMealPlanStructureInvalid({
                breakfasts: structureIssue.distribution.breakfast,
                lunches: structureIssue.distribution.lunch,
                dinners: structureIssue.distribution.dinner,
                unknown: structureIssue.distribution.unknown,
                recipe_count: completeMealPlan.length,
                retry_attempt: structureRetryCount,
                failure_reason: failureReason,
                parse_status: parseStatus,
                validation_source: validationSource,
                model: completionModel,
                provider: completionProvider,
                response_length_bucket: getResponseLengthBucket(parseMetadata.authoritativeResponseLength || parseMetadata.fullResponseLength),
                meal_type_sequence: mealTypeSequence,
              });
            } catch (_) { /* analytics non-fatal */ }

            if (structureRetryCount < MAX_STRUCTURE_RETRIES) {
              structureRetryCount++;
              retryReason = `${failureReason}: ${structureIssue.reason}; ${distributionSummary}`;
              abortProgressiveProcessor({ generationId: attemptId });
              startNextAttempt();
              return runGeneration();
            }

            abortProgressiveProcessor({ generationId: attemptId });
            throw new Error(`Meal plan structure validation failed after retries: ${failureReason}: ${structureIssue.reason}; ${distributionSummary}`);
          }

          if (positionIssues.length > 0) {
            console.warn(
              `[MealPlan] ⚠️ Meal type slot order mismatch (diagnostic only): ${positionIssues.map(item => `#${item.position} expected ${item.expected}, got ${item.actual}`).join('; ')}; sequence=${mealTypeSequenceText}`
            );
          }

          const timeViolations = getCookingTimeViolations(completeMealPlan, cookingStyle);
          if (timeViolations.length > 0) {
            const summary = timeViolations
              .map(item => item.reason
                ? `${item.mealType} "${item.title}" (${item.reason}; declared ${item.minutes}min, limit ${item.limit}min, ${item.complexity})`
                : `${item.mealType} "${item.title}" ${item.minutes}min > ${item.limit}min (${item.complexity})`)
              .join('; ');
            console.warn(`[MealPlan] ⚠️ Invalid cooking times: ${summary}; source=${validationSource}; parse=${parseStatus}; sequence=${mealTypeSequenceText}`);

            if (structureRetryCount < MAX_STRUCTURE_RETRIES) {
              structureRetryCount++;
              retryReason = `cooking_time: ${summary}`;
              abortProgressiveProcessor({ generationId: attemptId });
              startNextAttempt();
              return runGeneration();
            }

            abortProgressiveProcessor({ generationId: attemptId });
            throw new Error(`Meal plan cooking-time validation failed after retries: ${summary}`);
          }

          const dietaryViolations = getDietaryRestrictionViolations(completeMealPlan, dietaryPreferences);
          if (dietaryViolations.length > 0) {
            const summary = dietaryViolations
              .map(item => `${item.mealType} "${item.title}" uses forbidden text: ${item.offending.join(', ')}`)
              .join('; ');
            console.warn(`[MealPlan] ⚠️ Dietary restriction violation: ${summary}; source=${validationSource}; parse=${parseStatus}; sequence=${mealTypeSequenceText}`);

            if (structureRetryCount < MAX_STRUCTURE_RETRIES) {
              structureRetryCount++;
              retryReason = `dietary: ${summary}`;
              abortProgressiveProcessor({ generationId: attemptId });
              startNextAttempt();
              return runGeneration();
            }

            abortProgressiveProcessor({ generationId: attemptId });
            throw new Error(`Meal plan dietary validation failed after retries: ${summary}`);
          }
        }
      };

      try {
        await runGeneration();

        if (completeMealPlan.length > 0) {
          const finalAttemptId = currentAttemptId;
          if (!isCurrentAttempt(finalAttemptId)) return;
          if (!isMountedRef.current) return;
          setLoadingPhase("grocery");
          addReasoningStep(`Got all ${completeMealPlan.length} recipes, generating grocery list locally for ${userCountry}`);

          // Full grocery matching for the complete grocery list UI (totalCost, leftovers, scaling).
          // When Typesense Phase 2 completes mid-pipeline, release the held recipe details
          // queue so all GPT recipe-detail calls fire with real consolidated product data.
          const groceryResult = await generateGroceryList(completeMealPlan, {
            onTypesenseMatched: (matchedProducts) => {
              if (!isCurrentAttempt(finalAttemptId)) {
                console.log(`[MealPlan] ⏹️ Ignoring stale Typesense match for attempt ${finalAttemptId}`);
                return;
              }
              console.log(`[MealPlan] 🔓 Typesense matching complete — releasing recipe details queue with ${matchedProducts?.length || 0} categories`);
              releaseWithMatchedProducts(matchedProducts, { generationId: finalAttemptId });
            },
          });

          if (!isCurrentAttempt(finalAttemptId)) return;
          const matchedProducts = groceryResult?.products || [];
          console.log(`[MealPlan] 📦 Full grocery list done (${matchedProducts.length} categories). Per-recipe processing already in progress.`);
          addReasoningStep(`Full grocery list complete (${matchedProducts.length} categories). Recipe details already started per-recipe.`);

          // Apply accumulated storage data from recipe details that completed before grocery list
          if (storageDataRef.current.size > 0) {
            setProductList(prevProducts => {
              if (!prevProducts || prevProducts.length === 0) return prevProducts;
              let applied = 0;
              const enriched = prevProducts.map(cat => ({
                ...cat,
                items: cat.items.map(item => {
                  const productName = (item.productName || item.name || '').toLowerCase().trim();
                  const originalName = (item.originalName || '').toLowerCase().trim();
                  for (const [ingName, data] of storageDataRef.current) {
                    if (productName.includes(ingName) || ingName.includes(productName) ||
                        (originalName && (originalName.includes(ingName) || ingName.includes(originalName)))) {
                      applied++;
                      return mergeStorageDataIntoItem(item, data);
                    }
                  }
                  return item;
                }),
              }));
              if (applied > 0) {
                console.log(`[MealPlan] 🗄️ Post-grocery storage enrichment: applied storage data to ${applied} products`);
              }
              return enriched;
            });
          }

          // Safety net: queue any recipes that weren't dispatched via batches
          queueAllRecipesForProcessing(completeMealPlan, effectiveCountry, matchedProducts, { generationId: finalAttemptId });
        }

        if (!isMountedRef.current) return;

        trackMealSetGenerationSucceeded({
          recipe_count: completeMealPlan.length,
          total_cost: parseFloat(totalCost) || 0,
          generation_time_ms: Date.now() - generationStartTime,
          max_storage_days: mealPlanCtx.maxStorageTime || null,
          provider: 'openai',
        });

        setLoadingState("complete");
        endStreaming();
        // Single, authoritative cost summary for the entire user-visible generation
        // (includes any internal structure-retry attempts).
        try { generationCostTracker.printSummary(); } catch (_) { /* non-fatal */ }
      } catch (error) {
        if (!isMountedRef.current) return;
        console.error("Error loading meal plan:", error);
        abortProgressiveProcessor({ generationId: currentAttemptId });
        setImagesLoading({});
        trackMealSetGenerationFailed({
          error_stage: error.name === 'TimeoutError' ? ERROR_STAGE.TIMEOUT : ERROR_STAGE.API,
          error_code: error.status >= 500 ? ERROR_CODE.SERVER_5XX :
                      error.status >= 400 ? ERROR_CODE.CLIENT_4XX :
                      error.message?.includes('network') ? ERROR_CODE.NETWORK : ERROR_CODE.UNKNOWN,
          retry_count: structureRetryCount,
          http_status: error.status || null,
        });
        setLoadingState("error");
        endStreaming();
      }
    };

    if (!hasFetchedMealPlan.current && !restoredFromLocalStorage) {
      loadPlan();
      hasFetchedMealPlan.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    budget, numberOfPeople, selectedCuisine, dietaryPreferences, userCountry,
    setMealPlan, setTotalCost, setProductList, setMaxStorageTime,
    clearReasoningSteps, addReasoningStep, startStreaming, updateStreamProgress,
    endStreaming, generateGroceryList, setLoadingProgress,
    user, skipSavedMealPlan, restoredFromLocalStorage,
  ]);

  // ─── Check empty meal plan → redirect ─────────────────────
  useEffect(() => {
    const checkEmptyMealPlan = () => {
      if (restoredFromLocalStorage || isFromWizard) return;
      if (loadingState === "complete") {
        const hasValidRecipes = mealPlan && mealPlan.length > 0 && mealPlan.some(r => !r.isPlaceholder);
        if (!hasValidRecipes && onNavigateToWizard) {
          console.log("✅ Initial load: Meal plan is empty, redirecting to UserWizard");
          onNavigateToWizard();
        }
      }
    };
    const timeoutId = setTimeout(checkEmptyMealPlan, 500);
    return () => clearTimeout(timeoutId);
  }, [mealPlan, loadingState, onNavigateToWizard, restoredFromLocalStorage, isFromWizard]);

  // ─── Reset handler ────────────────────────────────────────
  const handleReset = useCallback(async () => {
    const { clearAlternativesCache } = await import("../AlternativeRecipes");
    clearAlternativesCache();
    resetMealPlan();
    hasFetchedMealPlan.current = false;
    setLoadingProgress(0);
    clearReasoningSteps();
    setTotalCost("");
    setAdjustedTotalCost(null);
    setSkipSavedMealPlan(true);
    if (onNavigateToWizard) {
      onNavigateToWizard();
    } else {
      window.location.reload();
    }
  }, [resetMealPlan, clearReasoningSteps, setTotalCost, setAdjustedTotalCost, setLoadingProgress, onNavigateToWizard]);

  return {
    loadingState,
    loadingPhase,
    loadingRecipesReady,
    wasLoadedFromDb,
    setWasLoadedFromDb,
    restoredFromLocalStorage,
    isPremium,
    isAdmin,
    triesLeft,
    replacementsRemaining,
    maxReplacements,
    currentDescriptionIndex,
    rotatingDescriptions,
    handleReset,
  };
}
